<template>
  <div v-if="invoice_id" class="rs-page">

    <!-- ===================================================== -->
    <!-- LOADING                                               -->
    <!-- ===================================================== -->
    <transition name="rs-fade">
      <div v-if="items_being_loaded" class="rs-loading">
        <div class="rs-loading-card">
          <div class="rs-logo">FM</div>
          <div class="rs-loading-text">Loading refunds…</div>
          <div class="rs-loading-bar">
            <div class="rs-loading-fill" style="width: 100%"></div>
          </div>
        </div>
      </div>
    </transition>

    <!-- ===================================================== -->
    <!-- EMPTY                                                 -->
    <!-- ===================================================== -->
    <div v-if="!items_being_loaded && items.length === 0" class="rs-empty">
      <b-icon icon="clipboard-x" font-scale="1.8"></b-icon>
      <span>No refunds history for this invoice.</span>
    </div>

    <!-- ===================================================== -->
    <!-- LIST — two columns: credit left, refund right         -->
    <!-- ===================================================== -->
    <div v-else class="rs-columns">

      <!-- ============ LEFT: STORE CREDIT ============ -->
      <section v-if="credit_items.length" class="rs-col is-credit">
        <div class="rs-col-title">
          <span class="rs-icon is-return"><b-icon icon="arrow-return-left"></b-icon></span>
          Store credit
        </div>

        <article
          v-for="(item, ridx) in credit_items"
          :key="'c-' + ridx"
          class="rs-card is-return"
        >
          <header class="rs-head">
            <div class="rs-head-left">
              <span class="rs-chip">
                <b-icon icon="calendar-event"></b-icon> {{ item.detail.ts }}
              </span>
              <span class="rs-chip is-teal">
                <b-icon icon="person-badge"></b-icon>
                {{ (item.detail.created_by || '').toUpperCase() || '—' }}
              </span>
            </div>
            <div class="rs-head-total">
              <span class="rs-head-total-label">Total</span>
              <span class="rs-head-total-value">
                ${{ (get_total_for_a_refund(item.detail) || 0.0).toFixed(2) }}
              </span>
            </div>
          </header>

          <div class="rs-body">
            <div class="rs-grid">
              <div class="rs-table-wrap">
                <table class="rs-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th class="rs-num">Qty</th>
                      <th class="rs-num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(ritem, rtidx) in item.ritems" :key="rtidx">
                      <td>{{ ritem.product_name }}</td>
                      <td><span class="rs-sku">{{ ritem.product_sku }}</span></td>
                      <td class="rs-num">{{ ritem.return_quantity }}</td>
                      <td class="rs-num">
                        <span class="rs-amt">${{ (ritem.selling_price || 0.0).toFixed(2) }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <ul class="rs-charges">
                <li>
                  <span>Delivery</span>
                  <b>${{ (item.detail.total_refunded_delivery || 0.0).toFixed(2) }}</b>
                </li>
                <li>
                  <span>Setup</span>
                  <b>${{ (item.detail.total_refunded_setup || 0.0).toFixed(2) }}</b>
                </li>
                <li>
                  <span>Warranty</span>
                  <b>${{ (item.detail.total_refunded_warranty || 0.0).toFixed(2) }}</b>
                </li>
                <li>
                  <span>Sales tax</span>
                  <b>${{ (item.detail.total_refunded_sales_tax || 0.0).toFixed(2) }}</b>
                </li>
              </ul>
            </div>
          </div>
        </article>
      </section>

      <!-- ============ RIGHT: REFUND ============ -->
      <section v-if="refund_items.length" class="rs-col is-refund">
        <div class="rs-col-title">
          <span class="rs-icon is-refund"><b-icon icon="cash-stack"></b-icon></span>
          Refund
        </div>

        <article
          v-for="(item, ridx) in refund_items"
          :key="'r-' + ridx"
          class="rs-card is-refund"
        >
          <header class="rs-head">
            <div class="rs-head-left">
              <span class="rs-chip">
                <b-icon icon="calendar-event"></b-icon> {{ item.detail.ts }}
              </span>
              <span class="rs-chip is-teal">
                <b-icon icon="person-badge"></b-icon>
                {{ (item.detail.processing_user || '').toUpperCase() || '—' }}
              </span>
            </div>
            <div class="rs-head-total">
              <span class="rs-head-total-label">Total</span>
              <span class="rs-head-total-value is-teal">
                ${{ (parseFloat(item.detail.amount) || 0.0).toFixed(2) }}
              </span>
            </div>
          </header>

          <div class="rs-body">
            <ul class="rs-payouts">
              <li v-for="(ritem, rtidx) in item.ritems" :key="rtidx">
                <span class="rs-method-chip" :class="'is-' + (ritem.value || '').toLowerCase()">
                  {{ (ritem.value || '').toUpperCase() }}
                </span>
                <b class="rs-amt is-teal">
                  ${{ parseFloat(ritem.issue_amount || 0.0).toFixed(2) }}
                </b>
              </li>
            </ul>
          </div>
        </article>
      </section>

    </div>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

export default {
  name: "RefundsSummary",
  props: ["viewdata"],
  components: { Requestor, Loading },
  data: function () {
    return {
      packet: {},
      invoice_id: null,
      items: [],
      items_being_loaded: null,
    };
  },
  computed: {
    credit_items: function () {
      return this.items.filter(function (i) {
        return i && i.detail && i.detail.type === 'return';
      });
    },
    refund_items: function () {
      return this.items.filter(function (i) {
        return i && i.detail && i.detail.type === 'refund';
      });
    },
  },
  methods: {
    fetch_init: function () {
      let path = "/api/sales/get_refunds_history/";
      let data = { invoice_id: this.invoice_id };
      let that = this;

      let s = function (response) {
        if (response.data.result) {
          that.items = response.data.result;
          that.$emit("refunds-history", that.items);
        } else {
          that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        }
        that.items_being_loaded = false;
      };

      let f = function () {
        that.$bvToast.toast("Error loading refunds history.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
        that.items_being_loaded = false;
      };

      this.items = [];
      this.items_being_loaded = true;
      Requestor.getAsyncP(path, data, this, s, f);
    },

    get_total_for_a_refund: function (item) {
      return (
        (item.total_refunded_item_amount || 0.0) +
        (item.total_refunded_delivery || 0.0) +
        (item.total_refunded_setup || 0.0) +
        (item.total_refunded_warranty || 0.0) +
        (item.total_refunded_sales_tax || 0.0)
      );
    },
  },
  created: function () {
    if (this.viewdata.invoice_id) {
      this.invoice_id = this.viewdata.invoice_id;
      this.packet = this.viewdata.packet;
      this.fetch_init();
    }
  },
};
</script>

<style scoped>
/* ===================================================== */
/* SHELL                                                 */
/* ===================================================== */
.rs-page {
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #221F1C;
  font-size: 14px;
}

/* ===================================================== */
/* LOADING                                               */
/* ===================================================== */
.rs-loading { display: flex; justify-content: center; padding: 20px 0; }
.rs-loading-card {
  background: #fff; padding: 18px 28px; border-radius: 14px;
  text-align: center; border-bottom: 4px solid #EE7A22;
  min-width: 240px;
}
.rs-logo {
  width: 44px; height: 44px; border-radius: 12px;
  background: #EE7A22; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-family: 'Sora', sans-serif; font-weight: 800; font-size: 16px;
  margin-bottom: 10px; animation: rsPulse 1.2s ease-in-out infinite;
}
@keyframes rsPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(0.92); } }
.rs-loading-text { font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 700; margin-bottom: 10px; }
.rs-loading-bar { width: 180px; height: 5px; background: #F1EEE9; border-radius: 3px; overflow: hidden; margin: 0 auto; }
.rs-loading-fill { height: 100%; background: #EE7A22; border-radius: 3px; }

.rs-fade-enter-active, .rs-fade-leave-active { transition: opacity 0.2s ease; }
.rs-fade-enter, .rs-fade-leave-to { opacity: 0; }

/* ===================================================== */
/* EMPTY                                                 */
/* ===================================================== */
.rs-empty {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 18px;
  background: #FAF9F7;
  border: 1.5px dashed #D6CFC4;
  border-radius: 12px;
  color: #736C63;
  font-size: 14px;
  font-weight: 600;
}
.rs-empty .b-icon { color: #EE7A22; font-size: 22px; }

/* ===================================================== */
/* OUTER TWO-COLUMN LAYOUT                               */
/* ===================================================== */
.rs-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  align-items: start;
}
.rs-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.rs-col-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Sora', sans-serif;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #736C63;
}
.rs-col-title .rs-icon { width: 26px; height: 26px; border-radius: 8px; font-size: 12px; }

/* ===================================================== */
/* CARDS                                                 */
/* ===================================================== */
.rs-card {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 12px;
  overflow: hidden;
  animation: rsCardIn 0.28s ease-out backwards;
}
.rs-card.is-return { border-left: 4px solid #1d140d; }
.rs-card.is-refund { border-left: 4px solid #0F6E6B; }
@keyframes rsCardIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ---------- HEADER ---------- */
.rs-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
  background: #F7F5F2;
  border-bottom: 1px solid #E5E1DA;
}
.rs-head-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.rs-icon {
  width: 32px; height: 32px;
  border-radius: 9px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 14px; flex-shrink: 0;
}
.rs-icon.is-return { background: #FFF1E4; color: #D4620F; }
.rs-icon.is-refund { background: #E2F2F0; color: #0F6E6B; }

.rs-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11.5px; font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #E5E1DA;
  color: #4A4540;
  white-space: nowrap;
}
.rs-chip.is-teal { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }

.rs-head-total {
  display: inline-flex; align-items: baseline; gap: 8px;
  background: #221F1C;
  padding: 5px 12px;
  border-radius: 999px;
}
.rs-head-total-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
}
.rs-head-total-value {
  font-family: 'Sora', sans-serif;
  font-size: 15px; font-weight: 800;
  color: #FFB06B;
  font-variant-numeric: tabular-nums;
}
.rs-head-total-value.is-teal { color: #7FDACF; }

/* ---------- BODY ---------- */
.rs-body { padding: 12px 14px; }

/* ---------- INNER GRID: table + 220px charges ---------- */
.rs-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 14px;
  align-items: start;
}

.rs-table-wrap {
  overflow-x: auto;
  border: 1px solid #E5E1DA;
  border-radius: 10px;
}
.rs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}
.rs-table thead th {
  background: #F7F5F2;
  color: #736C63;
  font-family: 'Sora', sans-serif;
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 7px 10px;
  text-align: left;
  border-bottom: 1px solid #E5E1DA;
  white-space: nowrap;
}
.rs-table tbody td {
  padding: 7px 10px;
  border-bottom: 1px solid #F1EEE9;
  color: #221F1C;
  vertical-align: middle;
}
.rs-table tbody tr:last-child td { border-bottom: 0; }

.rs-num { text-align: right; }

.rs-sku {
  font-family: 'Menlo', monospace;
  font-size: 11.5px;
  font-weight: 700;
  color: #D4620F;
  background: #FFF1E4;
  border: 1px solid #F5CFAE;
  padding: 1px 6px;
  border-radius: 5px;
  display: inline-block;
}

.rs-amt {
  font-family: 'Sora', sans-serif;
  font-size: 13.5px;
  font-weight: 800;
  color: #221F1C;
  font-variant-numeric: tabular-nums;
}
.rs-amt.is-teal { color: #0F6E6B; }

/* ---------- CHARGES COLUMN (fixed 220px) ---------- */
.rs-charges {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 220px;
  flex-shrink: 0;
}
.rs-charges li {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  padding: 5px 10px;
  background: #F7F5F2;
  border-radius: 8px;
  font-size: 12.5px;
}
.rs-charges li span { color: #736C63; font-weight: 600; }
.rs-charges li b {
  font-family: 'Sora', sans-serif;
  font-weight: 800;
  color: #221F1C;
  font-variant-numeric: tabular-nums;
}

/* ---------- REFUND PAYOUTS ---------- */
.rs-payouts {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.rs-payouts li {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px 5px 6px;
  background: #F7F5F2;
  border-radius: 999px;
  font-size: 12.5px;
}

.rs-method-chip {
  display: inline-flex; align-items: center;
  font-size: 11.5px; font-weight: 800;
  padding: 3px 9px; border-radius: 999px;
  background: #F1EEE9; color: #4A4540;
  border: 1px solid #E5E1DA;
  white-space: nowrap;
}
.rs-method-chip.is-cc           { background: #E8EFF7; color: #234A66; border-color: #BBD5E8; }
.rs-method-chip.is-cash         { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.rs-method-chip.is-finance      { background: #EEE6F9; color: #5E37A3; border-color: #D4C0F0; }
.rs-method-chip.is-store_credit { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }
.rs-method-chip.is-tax_exempt   { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }

/* ===================================================== */
/* RESPONSIVE                                            */
/* ===================================================== */
@media (max-width: 1200px) {
  /* inner table+charges can no longer hold 220px side-by-side → stack */
  .rs-grid { grid-template-columns: 1fr; }
  .rs-charges { width: 100%; }
}

@media (max-width: 900px) {
  /* outer two columns collapse to one */
  .rs-columns { grid-template-columns: 1fr; }
}

@media (max-width: 767px) {
  .rs-head { padding: 10px 12px; }
  .rs-body { padding: 10px 12px; }
  .rs-chip { font-size: 11px; padding: 2px 8px; }
  .rs-head-total-value { font-size: 14px; }
}

/* ===================================================== */
/* PRINT                                                 */
/* ===================================================== */
@media print {
  .rs-loading { display: none !important; }
  .rs-head {
    background: #fff !important;
    border-bottom: 1px solid #000;
  }
  .rs-head-total { background: #efefef; }
  .rs-head-total-value, .rs-head-total-value.is-teal { color: #000 !important; }
  .rs-chip { background: #efefef !important; color: #000 !important; border-color: #999 !important; }
}

@media (prefers-reduced-motion: reduce) {
  .rs-card, .rs-logo { animation: none !important; }
}
</style>


// WEBPACK FOOTER //
// src/components/importables/RefundsSummary.vue